<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength, email } from '@vuelidate/validators';
import { useBranding } from 'shared/composables/useBranding';
import { resetPassword } from '../../../../api/auth';
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

export default {
  components: { Spinner, Icon },
  setup() {
    const { replaceInstallationName } = useBranding();
    return { v$: useVuelidate(), replaceInstallationName };
  },
  data() {
    return {
      credentials: { email: '' },
      resetPasswordApi: {
        message: '',
        showLoading: false,
      },
      error: '',
    };
  },
  validations() {
    return {
      credentials: {
        email: {
          required,
          email,
          minLength: minLength(4),
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    loginBackgroundImage() {
      return this.globalConfig.loginBackgroundImage || null;
    },
    loginOverlayColor() {
      return this.globalConfig.loginOverlayColor || '#1f93ff';
    },
    loginOverlayOpacity() {
      const opacity = this.globalConfig.loginOverlayOpacity;
      return typeof opacity === 'number' ? opacity : 80;
    },
    backgroundStyle() {
      const defaultImage =
        'https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=2574&auto=format&fit=crop';
      const image = this.loginBackgroundImage || defaultImage;
      return {
        backgroundImage: `url('${image}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    },
    overlayStyle() {
      const hex = this.loginOverlayColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const opacity = this.loginOverlayOpacity / 100;

      return {
        background: `linear-gradient(to top, rgba(${r}, ${g}, ${b}, ${opacity * 0.9}) 0%, rgba(${r}, ${g}, ${b}, ${opacity * 0.5}) 50%, transparent 100%)`,
      };
    },
    brandColor() {
      return this.globalConfig.brandPrimaryColor || '#1f93ff';
    },
    buttonGradientStyle() {
      const hex = this.brandColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const lighterR = Math.min(255, r + 40);
      const lighterG = Math.min(255, g + 40);
      const lighterB = Math.min(255, b + 40);
      const darkerR = Math.max(0, r - 30);
      const darkerG = Math.max(0, g - 30);
      const darkerB = Math.max(0, b - 30);

      return {
        background: `linear-gradient(135deg, rgb(${lighterR}, ${lighterG}, ${lighterB}) 0%, rgb(${r}, ${g}, ${b}) 50%, rgb(${darkerR}, ${darkerG}, ${darkerB}) 100%)`,
      };
    },
  },
  methods: {
    showAlertMessage(message) {
      this.resetPasswordApi.showLoading = false;
      useAlert(message);
    },
    submit() {
      this.resetPasswordApi.showLoading = true;
      resetPassword(this.credentials)
        .then(res => {
          let successMessage = this.$t('RESET_PASSWORD.API.SUCCESS_MESSAGE');
          if (res.data && res.data.message) {
            successMessage = res.data.message;
          }
          this.showAlertMessage(successMessage);
        })
        .catch(error => {
          let errorMessage = this.$t('RESET_PASSWORD.API.ERROR_MESSAGE');
          if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          this.showAlertMessage(errorMessage);
        });
    },
  },
};
</script>

<template>
  <main class="relative min-h-screen w-full overflow-hidden">
    <!-- Fullscreen Background Image -->
    <div class="absolute inset-0" :style="backgroundStyle" />

    <!-- Configurable Overlay -->
    <div class="absolute inset-0" :style="overlayStyle" />

    <!-- Floating geometric shapes -->
    <div
      class="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse hidden lg:block"
    />
    <div
      class="absolute bottom-20 right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000 hidden lg:block"
    />

    <!-- Main Content Container -->
    <div
      class="relative z-10 min-h-screen flex items-center justify-center py-8 px-4"
    >
      <!-- Reset Card -->
      <div
        class="w-full max-w-md bg-white/40 backdrop-blur-xl rounded-3xl border border-black/10 shadow-2xl p-8"
      >
        <!-- Logo -->
        <div class="mb-6 flex justify-center">
          <img
            :src="globalConfig.logo || '/logo.png'"
            :alt="globalConfig.installationName"
            class="w-auto h-10"
          />
        </div>

        <!-- Title and Description -->
        <div class="mb-6 text-center">
          <h1 class="text-xl font-bold text-slate-900 mb-1">
            {{ $t('RESET_PASSWORD.TITLE') }}
          </h1>
          <p class="text-sm text-slate-600">
            {{ replaceInstallationName($t('RESET_PASSWORD.DESCRIPTION')) }}
          </p>
        </div>

        <!-- Form -->
        <form class="space-y-4" @submit.prevent="submit">
          <div class="space-y-1">
            <label class="block text-sm font-medium text-slate-700">
              {{ $t('LOGIN.EMAIL.LABEL') }}
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <Icon icon="i-lucide-at-sign" class="size-4 text-slate-400" />
              </div>
              <input
                v-model="credentials.email"
                type="email"
                name="email_address"
                data-testid="email_input"
                required
                :placeholder="$t('RESET_PASSWORD.EMAIL.PLACEHOLDER')"
                class="w-full pl-10 pr-4 py-3 bg-white/60 border border-black/10 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                :class="{
                  'ring-2 ring-red-500/50': v$.credentials.email.$error,
                }"
                @input="v$.credentials.email.$touch"
              />
            </div>
            <p
              v-if="v$.credentials.email.$error"
              class="mt-1 text-xs text-red-600"
            >
              {{ $t('RESET_PASSWORD.EMAIL.ERROR') }}
            </p>
          </div>

          <!-- Gradient Submit Button -->
          <button
            type="submit"
            data-testid="submit_button"
            :disabled="
              v$.credentials.email.$invalid || resetPasswordApi.showLoading
            "
            class="w-full py-3 mt-2 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            :style="buttonGradientStyle"
          >
            <span
              v-if="resetPasswordApi.showLoading"
              class="flex items-center justify-center gap-2"
            >
              <Spinner size="small" />
              <span>{{ $t('RESET_PASSWORD.SUBMIT') }}</span>
            </span>
            <span v-else>{{ $t('RESET_PASSWORD.SUBMIT') }}</span>
          </button>
        </form>

        <!-- Back to Login -->
        <p class="mt-5 text-center text-xs text-slate-600">
          {{ $t('RESET_PASSWORD.GO_BACK_TO_LOGIN') }}
          <router-link
            to="/app/login"
            class="font-medium text-blue-600 hover:text-blue-800 transition-colors ml-1"
          >
            {{ $t('COMMON.CLICK_HERE') }}
          </router-link>
        </p>

        <!-- Security Features -->
        <div class="mt-6 pt-5 border-t border-black/10">
          <div class="grid grid-cols-2 gap-3">
            <div class="flex items-center gap-2">
              <div
                class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                :style="{
                  background: `linear-gradient(135deg, ${brandColor}30, ${brandColor}15)`,
                }"
              >
                <Icon
                  icon="i-lucide-shield-check"
                  class="size-4 text-slate-700"
                />
              </div>
              <div class="min-w-0">
                <p class="text-xs font-medium text-slate-800 truncate">
                  Seguro
                </p>
                <p class="text-[10px] text-slate-500 truncate">
                  Link criptografado
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <div
                class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                :style="{
                  background: `linear-gradient(135deg, ${brandColor}30, ${brandColor}15)`,
                }"
              >
                <Icon icon="i-lucide-zap" class="size-4 text-slate-700" />
              </div>
              <div class="min-w-0">
                <p class="text-xs font-medium text-slate-800 truncate">
                  Rapido
                </p>
                <p class="text-[10px] text-slate-500 truncate">
                  Email em segundos
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-text-fill-color: #1e293b;
  -webkit-box-shadow: 0 0 0px 1000px rgba(255, 255, 255, 0.6) inset;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
